import { useState, useRef, useCallback } from 'react';

type TaskState = 'idle' | 'running' | 'error' | 'done' | 'stopped';

interface UseTaskManager {
    startTask: (name: string, taskFunction: () => Promise<void>) => void;
    stopTask: (name: string) => void;
    taskInProgress: Record<string, boolean>;
    getState: (name: string) => TaskState | undefined;
}

const useTaskManager = (): UseTaskManager => {
    const tasks = useRef<Record<string, { state: TaskState; controller?: AbortController }>>({});
    const [taskInProgress, setTaskInProgress] = useState<Record<string, boolean>>({});

    const startTask = useCallback((name: string, taskFunction: () => Promise<void>) => {
        if (tasks.current[name]?.state === 'running') {
            console.warn(`Task "${name}" is already running.`);
            return;
        }

        const controller = new AbortController();
        tasks.current[name] = { state: 'running', controller };
        setTaskInProgress((prev) => ({ ...prev, [name]: true }));

        taskFunction()
        .then(() => {
            if (tasks.current[name]?.state === 'running') {
                tasks.current[name].state = 'done';
                setTaskInProgress((prev) => ({ ...prev, [name]: false }));
            }
        })
        .catch((error) => {
            if (tasks.current[name]?.state === 'running') {
                tasks.current[name].state = 'error';
                setTaskInProgress((prev) => ({ ...prev, [name]: false }));
                console.error(`Task "${name}" failed:`, error);
            }
        });
    }, []);

    const stopTask = useCallback((name: string) => {
        if (!tasks.current[name]) {
            console.warn(`Task "${name}" does not exist.`);
            return;
        }

        if (tasks.current[name].controller) {
            tasks.current[name].controller?.abort();
        }

        tasks.current[name].state = 'stopped';
        setTaskInProgress((prev) => ({ ...prev, [name]: false }));
    }, []);

    const getState = useCallback((name: string) => {
        return tasks.current[name]?.state;
    }, []);

    return { startTask, stopTask, taskInProgress, getState };
};


export default useTaskManager